import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../../components/headers/Header';
import { getAllLeaves } from '../../services/leavesAPIs';
import ExportButtons from '../../components/btns/ExportButtons';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import marabesLogo from '../../assets/images/logos/Marabes.png';
import companyLogo from '../../assets/images/logos/Axopeople.png';

const DAY_MS = 24 * 60 * 60 * 1000;

const toDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (value) => {
  const d = toDateSafe(value);
  return d ? d.toLocaleDateString('en-GB') : 'N/A';
};

const getWeekStartMonday = (value) => {
  const date = toDateSafe(value);
  if (!date) return null;
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = local.getDay(); // 0 = Sun, 1 = Mon
  const diffToMonday = (day + 6) % 7;
  local.setDate(local.getDate() - diffToMonday);
  return local;
};

const formatWeekLabel = (weekStart) => {
  if (!weekStart) return 'N/A';
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return `${weekStart.toLocaleDateString('en-GB')} - ${end.toLocaleDateString('en-GB')}`;
};

const formatWeekTitle = (weekStart) => {
  if (!weekStart) return 'N/A';
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  return `Week of ${weekStart.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })} to ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
};

const getWeekDays = (weekStart) => {
  if (!weekStart) return [];
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
};

const getEndOfYear = (baseDate) => new Date(baseDate.getFullYear(), 11, 31);

const toDateKey = (date) => {
  if (!date) return '';
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return local.toISOString().slice(0, 10);
};

const getBusyInfo = (count) => {
  if (count >= 8) {
    return { label: 'Very Busy', color: 'error' };
  }
  if (count >= 4) {
    return { label: 'Busy', color: 'warning' };
  }
  if (count >= 1) {
    return { label: 'Normal', color: 'success' };
  }
  return { label: 'Free', color: 'default' };
};

const getIsoWeekNumber = (dateInput) => {
  const date = toDateSafe(dateInput);
  if (!date) return null;
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / DAY_MS) + 1) / 7);
};

const getEmployeeFullName = (leave) => {
  const first = leave?.employeeFirstName || '';
  const last = leave?.employeeLastName || '';
  const full = `${first} ${last}`.trim();
  return full || 'Unknown';
};

const getEmployeeProjects = (leave) => {
  const employee = leave?.employee || {};

  const rawProjects =
    employee.projects ||
    employee.assignedProjects ||
    leave.projects ||
    leave.assignedProjects ||
    [];

  const projectsFromArray = Array.isArray(rawProjects)
    ? rawProjects
        .map((p) => {
          if (typeof p === 'string') return p;
          return p?.projectName || p?.name || p?.title || '';
        })
        .filter(Boolean)
    : [];

  if (projectsFromArray.length > 0) {
    return projectsFromArray.join(', ');
  }

  if (employee.projectName) return employee.projectName;
  if (employee.project?.name) return employee.project.name;
  if (leave.project?.name) return leave.project.name;
  if (leave.projectName) return leave.projectName;
  return 'N/A';
};

const getWeekEmployeeDetails = (requests = []) => {
  const employeeMap = {};

  requests.forEach((leave) => {
    const employeeKey = leave?.employee?.employeeId || getEmployeeFullName(leave);
    if (!employeeMap[employeeKey]) {
      employeeMap[employeeKey] = {
        name: getEmployeeFullName(leave),
        department: leave?.employee?.department?.name || 'N/A',
        joiningDate: leave?.employee?.dateOfJoining || null,
        projects: getEmployeeProjects(leave),
        count: 0
      };
    }
    employeeMap[employeeKey].count += 1;
  });

  return Object.values(employeeMap).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

const getSubmissionAge = (createdAt) => {
  const created = toDateSafe(createdAt);
  if (!created) {
    return {
      label: 'Unknown',
      color: 'default',
      hint: 'Submission date not available'
    };
  }

  const now = new Date();
  const createdDay = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.max(0, Math.floor((nowDay - createdDay) / DAY_MS));

  if (days === 0) {
    return {
      label: 'Today',
      color: 'success',
      hint: `Submitted today (${formatDate(createdAt)})`
    };
  }
  if (days <= 2) {
    return {
      label: `${days} day${days > 1 ? 's' : ''} ago`,
      color: 'success',
      hint: `Submitted ${days} day${days > 1 ? 's' : ''} ago (${formatDate(createdAt)})`
    };
  }
  if (days <= 7) {
    return {
      label: `${days} days ago`,
      color: 'warning',
      hint: `Submitted ${days} days ago (${formatDate(createdAt)})`
    };
  }
  return {
    label: `${days} days ago`,
    color: 'error',
    hint: `Older pending request: ${days} days (${formatDate(createdAt)})`
  };
};

const VacationRequestsOverview = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [selectedWeekKey, setSelectedWeekKey] = useState('');
  const [openWeekDialog, setOpenWeekDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const exportPDF = async () => {
    setIsExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      const data = selectedRequests;
      if (!data || data.length === 0) {
        throw new Error('No data available to export');
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = 0;
      let currentPage = 1;

      const addPDFHeader = (pageNum) => {
        if (pageNum === 1) {
          const headerHeight = 22;
          doc.setFillColor(248, 250, 252);
          doc.rect(0, 0, pageWidth, headerHeight, 'F');

          const logoWidth = 28;
          const logoHeight = 11;
          const logoY = (headerHeight - logoHeight) / 2;

          try { doc.addImage(marabesLogo, 'PNG', margin, logoY, logoWidth, logoHeight); } catch (e) { }
          try { doc.addImage(companyLogo, 'PNG', pageWidth - margin - logoWidth, logoY, logoWidth, logoHeight); } catch (e) { }

          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(30, 41, 59);
          doc.text(`WEEK ${getIsoWeekNumber(selectedWeek?.weekStart)} VACATION REQUESTS`, pageWidth / 2, 11.5, { align: 'center' });

          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text(`Generated: ${new Date().toLocaleDateString('en-GB')}`, pageWidth / 2, 17.5, { align: 'center' });
        }
      };

      const addPDFFooter = () => {
        doc.setLineWidth(0.5);
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
        doc.setFontSize(7);
        doc.setTextColor(148, 163, 184);
        doc.text('Confidential - For Internal Use Only', pageWidth / 2, pageHeight - 15, { align: 'center' });
        doc.text(`Total Records: ${data.length}`, margin, pageHeight - 10, { align: 'left' });
      };

      addPDFHeader(currentPage);
      yPos = 27;

      const tableHeaders = [
        { label: 'Employee', width: 20 },
        { label: 'Office', width: 12 },
        { label: 'Dept', width: 15 },
        { label: 'Type', width: 15 },
        { label: 'Start', width: 12 },
        { label: 'End', width: 12 },
        { label: 'Submitted', width: 14 }
      ];

      const colPositions = [];
      let xPos = margin;
      tableHeaders.forEach(header => {
        colPositions.push(xPos);
        xPos += (header.width / 100) * contentWidth;
      });

      const addTableHeader = () => {
        doc.setFillColor(30, 41, 59);
        const headerHeight = 10;
        doc.rect(margin, yPos, contentWidth, headerHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        tableHeaders.forEach((header, index) => {
          doc.text(header.label, colPositions[index] + 2, yPos + 6, { align: 'left' });
        });
        yPos += headerHeight;
      };

      const addTableRow = (leave, isEven) => {
        const rowHeight = 10;
        if (isEven) {
          doc.setFillColor(248, 250, 252);
          doc.rect(margin, yPos, contentWidth, rowHeight, 'F');
        }
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');

        const typeValue = (leave?.type || 'N/A').replace(/_/g, ' ').toLowerCase();
        const formattedType = typeValue.charAt(0).toUpperCase() + typeValue.slice(1);

        const rowData = [
          getEmployeeFullName(leave),
          leave?.employee?.office?.name || leave?.employee?.location || '-',
          leave?.employee?.department?.name || '-',
          formattedType,
          formatDate(leave?.startDate),
          formatDate(leave?.endDate),
          formatDate(leave?.createdAt)
        ];

        rowData.forEach((cell, index) => {
          let text = cell;
          const maxWidth = (tableHeaders[index].width / 100) * contentWidth - 4;
          if (doc.getTextWidth(text) > maxWidth) {
            const splitText = doc.splitTextToSize(text, maxWidth);
            text = splitText[0];
            if (splitText.length > 1) {
              text = text.substring(0, text.length - 2) + '..';
            }
          }
          doc.text(text, colPositions[index] + 2, yPos + 6, { align: 'left', maxWidth: maxWidth });
        });
        yPos += rowHeight;
      };

      addTableHeader();

      data.forEach((leave, index) => {
        if (yPos + 12 > pageHeight - 20) {
          addPDFFooter();
          doc.addPage();
          currentPage++;
          addPDFHeader(currentPage);
          yPos = 20;
          addTableHeader();
        }
        addTableRow(leave, index % 2 === 0);
      });

      addPDFFooter();

      for (let i = 1; i <= currentPage; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(`Page ${i} of ${currentPage}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
      }
      doc.setPage(1);

      const fileName = `Week_${getIsoWeekNumber(selectedWeek?.weekStart)}_Requests_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('PDF export failed:', error);
      setError(`PDF export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const exportExcel = () => {
    try {
      const data = selectedRequests;
      if (!data || data.length === 0) return;

      const excelData = data.map(leave => ({
        'Employee': getEmployeeFullName(leave),
        'Office': leave?.employee?.office?.name || leave?.employee?.location || 'N/A',
        'Department': leave?.employee?.department?.name || 'N/A',
        'Joining Date': formatDate(leave?.employee?.dateOfJoining),
        'Projects': getEmployeeProjects(leave),
        'Leave Type': (leave?.type || 'N/A').replace(/_/g, ' '),
        'Start Date': formatDate(leave?.startDate),
        'End Date': formatDate(leave?.endDate),
        'Submitted On': formatDate(leave?.createdAt)
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
        fill: { fgColor: { rgb: '1E293B' } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { rgb: 'FFFFFF' } },
          left: { style: 'thin', color: { rgb: 'FFFFFF' } },
          right: { style: 'thin', color: { rgb: 'FFFFFF' } }
        }
      };

      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = { c: C, r: 0 };
        const cellRef = XLSX.utils.encode_cell(cellAddress);
        if (!ws[cellRef]) continue;
        ws[cellRef].s = headerStyle;
      }

      const colWidths = [];
      excelData.forEach(row => {
        Object.entries(row).forEach(([key, value], index) => {
          const cellValue = value?.toString() || '';
          const width = Math.min(30, Math.max(12, cellValue.length + 2));
          if (!colWidths[index] || width > colWidths[index]) {
            colWidths[index] = width;
          }
        });
      });
      ws['!cols'] = colWidths.map(w => ({ wch: w }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Week Requests');
      const fileName = `Week_${getIsoWeekNumber(selectedWeek?.weekStart)}_Requests_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Excel export failed:', error);
      setError(`Excel export failed: ${error.message || 'Unknown error'}`);
    }
  };

  useEffect(() => {
    const fetchPendingRequests = async () => {
      setLoading(true);
      setError('');
      try {
        const allLeaves = await getAllLeaves();
        const onlyPending = (allLeaves || []).filter(
          (leave) => (leave?.status || '').toUpperCase() === 'PENDING'
        );
        setPendingLeaves(onlyPending);
      } catch (e) {
        setError('Failed to load vacation requests overview.');
      } finally {
        setLoading(false);
      }
    };

    fetchPendingRequests();
  }, []);

  const weeks = useMemo(() => {
    const now = new Date();
    const currentWeekStart = getWeekStartMonday(now);
    const endOfYear = getEndOfYear(now);
    if (!currentWeekStart) return [];

    const weekMap = {};
    const generatedWeeks = [];

    for (let cursor = new Date(currentWeekStart); cursor <= endOfYear; cursor.setDate(cursor.getDate() + 7)) {
      const weekStart = new Date(cursor);
      const key = toDateKey(weekStart);
      const baseWeek = {
        key,
        weekStart,
        weekLabel: formatWeekLabel(weekStart),
        requests: []
      };
      weekMap[key] = baseWeek;
      generatedWeeks.push(baseWeek);
    }

    pendingLeaves.forEach((leave) => {
      const startDate = toDateSafe(leave?.startDate);
      if (!startDate) return;
      if (startDate < currentWeekStart || startDate > endOfYear) return;

      const weekStart = getWeekStartMonday(startDate);
      const key = toDateKey(weekStart);
      if (weekMap[key]) {
        weekMap[key].requests.push(leave);
      }
    });

    return generatedWeeks.map((group) => {
      const uniqueEmployees = new Set(
        group.requests.map((leave) => leave?.employee?.employeeId || getEmployeeFullName(leave))
      ).size;
      const uniqueDepartments = new Set(
        group.requests
          .map((leave) => leave?.employee?.department?.name)
          .filter(Boolean)
      ).size;
      return {
        ...group,
        totalRequests: group.requests.length,
        uniqueEmployees,
        uniqueDepartments
      };
    });
  }, [pendingLeaves]);

  useEffect(() => {
    if (!weeks.length) {
      setSelectedWeekKey('');
      return;
    }
    const currentWeekKey = toDateKey(getWeekStartMonday(new Date()));
    setSelectedWeekKey((prev) => {
      if (prev && weeks.some((w) => w.key === prev)) return prev;
      if (currentWeekKey && weeks.some((w) => w.key === currentWeekKey)) return currentWeekKey;
      return weeks[0].key;
    });
  }, [weeks]);

  const selectedWeek = weeks.find((w) => w.key === selectedWeekKey) || null;
  const selectedRequests = useMemo(() => {
    if (!selectedWeek) return [];
    return [...selectedWeek.requests].sort((a, b) => {
      const aDate = toDateSafe(a?.createdAt) || toDateSafe(a?.startDate) || new Date(0);
      const bDate = toDateSafe(b?.createdAt) || toDateSafe(b?.startDate) || new Date(0);
      return bDate - aDate;
    });
  }, [selectedWeek]);

  const selectedWeekEmployeeSummary = useMemo(() => {
    const acc = {};
    selectedRequests.forEach((leave) => {
      const name = getEmployeeFullName(leave);
      if (!acc[name]) {
        acc[name] = { name, count: 0, department: leave?.employee?.department?.name || 'N/A' };
      }
      acc[name].count += 1;
    });
    return Object.values(acc).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [selectedRequests]);

  const selectedWeekDays = useMemo(() => getWeekDays(selectedWeek?.weekStart), [selectedWeek]);

  const requestsByStartDate = useMemo(() => {
    const map = {};
    selectedRequests.forEach((leave) => {
      const d = toDateSafe(leave?.startDate);
      if (!d) return;
      const key = toDateKey(d);
      if (!map[key]) map[key] = [];
      map[key].push(leave);
    });
    return map;
  }, [selectedRequests]);

  const hasAnyPendingInTimeline = useMemo(
    () => weeks.some((week) => week.totalRequests > 0),
    [weeks]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Vacation Requests Overview"
        subtitle="Weekly readable view to support approval decisions"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!error && !hasAnyPendingInTimeline && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No pending vacation requests from the current week to end of year.
        </Alert>
      )}

      {weeks.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Timeline from current week to end of year (pending requests only)
          </Typography>
          <Box
            sx={{
              mb: 3,
              display: 'grid',
              gap: 1.25,
              gridTemplateColumns: {
                xs: 'repeat(1, minmax(0, 1fr))',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))'
              }
            }}
          >
            {weeks.map((week) => {
              const weekNumber = getIsoWeekNumber(week.weekStart);
              return (
              <Box key={week.key} sx={{ position: 'relative' }}>
                <Paper
                  onClick={() => {
                    setSelectedWeekKey(week.key);
                    setOpenWeekDialog(true);
                  }}
                  sx={{
                    p: 2.25,
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: 178,
                    border: week.key === selectedWeekKey ? '2px solid #2563eb' : '1px solid #e2e8f0',
                    backgroundColor: week.key === selectedWeekKey ? '#eff6ff' : '#ffffff',
                    transition: 'all 0.2s ease',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, letterSpacing: 0.4, color: '#1d4ed8', display: 'block' }}
                  >
                    Week {weekNumber || '-'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.75, lineHeight: 1.2 }}>
                    {week.weekLabel}
                  </Typography>
                  <Chip
                    size="small"
                    color={getBusyInfo(week.totalRequests).color}
                    label={getBusyInfo(week.totalRequests).label}
                    variant={week.totalRequests ? 'filled' : 'outlined'}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
                    Requests: {week.totalRequests}
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
                    Employees: {week.uniqueEmployees}
                  </Typography>
                  <Typography variant="body2" sx={{ display: 'block', fontWeight: 600 }}>
                    Departments: {week.uniqueDepartments}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    {week.requests
                      .slice(0, 3)
                      .map((leave) => getEmployeeFullName(leave))
                    .join(', ') || 'No employee'}
                    {week.requests.length > 3 ? ` +${week.requests.length - 3}` : ''}
                  </Typography>
                </Paper>
              </Box>
              );
            })}
          </Box>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {selectedWeek ? formatWeekTitle(selectedWeek.weekStart) : 'Week Details'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Calendar view shows day-by-day request load. Submission indicator highlights aging requests.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, borderColor: '#dbeafe', background: '#f8fbff' }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Employees This Week (Name + Number of Requests)
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {selectedWeekEmployeeSummary.map((item) => (
                  <Chip
                    key={item.name}
                    label={`${item.name} (${item.count}) • ${item.department}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </Paper>

            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, borderColor: '#e2e8f0', background: '#ffffff' }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Weekly Calendar
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(120px, 1fr))',
                  gap: 1
                }}
              >
                {selectedWeekDays.map((day) => {
                  const dateKey = day.toISOString().slice(0, 10);
                  const dayRequests = requestsByStartDate[dateKey] || [];
                  const names = dayRequests.map((leave) => getEmployeeFullName(leave));
                  return (
                    <Paper
                      key={dateKey}
                      variant="outlined"
                      sx={{
                        p: 1.25,
                        minHeight: 104,
                        borderColor: dayRequests.length ? '#93c5fd' : '#e5e7eb',
                        backgroundColor: dayRequests.length ? '#eff6ff' : '#f9fafb'
                      }}
                    >
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {day.toLocaleDateString('en-GB', { weekday: 'short' })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {day.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                        {dayRequests.length} request{dayRequests.length === 1 ? '' : 's'}
                      </Typography>
                      {names.slice(0, 2).map((name, idx) => (
                        <Typography key={`${dateKey}-${name}-${idx}`} variant="caption" sx={{ display: 'block' }}>
                          {name}
                        </Typography>
                      ))}
                      {names.length > 2 && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          +{names.length - 2} more
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Paper>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Office</TableCell>
                    <TableCell>Department</TableCell>
                    {/* <TableCell>Projects</TableCell> */}
                    {/* <TableCell>Joining Date</TableCell> */}
                    <TableCell>Leave Type</TableCell>
                    <TableCell>Leave Dates</TableCell>
                    <TableCell>Submitted On</TableCell>
                    <TableCell>Submission Age</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedRequests.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                        No pending requests in this week.
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedRequests.map((leave) => {
                    const age = getSubmissionAge(leave?.createdAt);
                    return (
                      <TableRow key={leave?.requestId || `${leave?.employeeId}-${leave?.startDate}`}>
                        <TableCell>{getEmployeeFullName(leave)}</TableCell>
                        <TableCell>{leave?.officeName || leave?.employee?.location || 'N/A'}</TableCell>
                        <TableCell>{leave?.departmentName || 'N/A'}</TableCell>
                        {/* <TableCell>{getEmployeeProjects(leave)}</TableCell> */}
                        {/* <TableCell>{formatDate(leave?.employee?.dateOfJoining)}</TableCell> */}
                        <TableCell>{(leave?.type || 'N/A').replace(/_/g, ' ')}</TableCell>
                        <TableCell>
                          {formatDate(leave?.startDate)} - {formatDate(leave?.endDate)}
                        </TableCell>
                        <TableCell>{formatDate(leave?.createdAt)}</TableCell>
                        <TableCell>
                          <Tooltip title={age.hint}>
                            <Chip
                              size="small"
                              color={age.color}
                              label={age.label}
                              variant={age.color === 'default' ? 'outlined' : 'filled'}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Dialog
            open={openWeekDialog && Boolean(selectedWeek)}
            onClose={() => setOpenWeekDialog(false)}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pr: 1
              }}
            >
              {selectedWeek
                ? `Week ${getIsoWeekNumber(selectedWeek.weekStart)} • ${selectedWeek.weekLabel}`
                : 'Week Details'}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedRequests.length > 0 && (
                  <ExportButtons 
                    onExportPDF={exportPDF} 
                    onExportExcel={exportExcel} 
                    isExporting={isExporting} 
                  />
                )}
                <IconButton
                  aria-label="Close dialog"
                  onClick={() => setOpenWeekDialog(false)}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedWeek && (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                    <Chip label={`Requests: ${selectedWeek.totalRequests}`} color="primary" />
                    <Chip label={`Employees: ${selectedWeek.uniqueEmployees}`} color="secondary" />
                    <Chip label={`Departments: ${selectedWeek.uniqueDepartments}`} color="secondary" />
                    <Chip
                      label={`Load: ${getBusyInfo(selectedWeek.totalRequests).label}`}
                      color={getBusyInfo(selectedWeek.totalRequests).color}
                    />
                  </Stack>

                  <Paper variant="outlined" sx={{ p: 2, mb: 2, background: '#f8fbff', borderColor: '#dbeafe' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                      Employees in this week
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {getWeekEmployeeDetails(selectedWeek.requests).map((emp) => (
                        <Chip
                          key={emp.name}
                          color="primary"
                          variant="outlined"
                          label={`${emp.name} (${emp.count}) • ${emp.department}`}
                        />
                      ))}
                    </Stack>
                  </Paper>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
                    Request details (for approval decision)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Employee</TableCell>
                          <TableCell>Office</TableCell>
                          <TableCell>Department</TableCell>
                          {/* <TableCell>Joining Date</TableCell> */}
                          {/* <TableCell>Projects</TableCell> */}
                          <TableCell>Leave Type</TableCell>
                          <TableCell>Leave Dates</TableCell>
                          <TableCell>Submitted On</TableCell>
                          <TableCell>Submission Age</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedRequests.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              No pending requests in this week.
                            </TableCell>
                          </TableRow>
                        )}
                        {selectedRequests.map((leave) => {
                          const age = getSubmissionAge(leave?.createdAt);
                          return (
                            <TableRow key={`dlg-${leave?.requestId || `${leave?.employeeId}-${leave?.startDate}`}`}>
                              <TableCell sx={{ fontWeight: 700 }}>{getEmployeeFullName(leave)}</TableCell>
                              <TableCell>{leave?.officeName || leave?.employee?.location || 'N/A'}</TableCell>
                              <TableCell>{leave?.departmentName || 'N/A'}</TableCell>
                              {/* <TableCell>{formatDate(leave?.employee?.dateOfJoining)}</TableCell> */}
                              {/* <TableCell>{getEmployeeProjects(leave)}</TableCell> */}
                              <TableCell>{(leave?.type || 'N/A').replace(/_/g, ' ')}</TableCell>
                              <TableCell>
                                {formatDate(leave?.startDate)} - {formatDate(leave?.endDate)}
                              </TableCell>
                              <TableCell>{formatDate(leave?.createdAt)}</TableCell>
                              <TableCell>
                                <Tooltip title={age.hint}>
                                  <Chip
                                    size="small"
                                    color={age.color}
                                    label={age.label}
                                    variant={age.color === 'default' ? 'outlined' : 'filled'}
                                  />
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default VacationRequestsOverview;
